// One run of the game as a disposable object (plan 17): the World, the cube
// meshes built from its blocks, and the dirty-subscription that rebuilds
// them. `main.ts`'s frame loop asks the *current* session for its world and
// meshes instead of closing over module globals — which is what lets the
// shell throw a run away (menu -> game now; game -> menu in the next shell
// plan) without a page reload.
//
// Layering note: `shell.ts` (the state machine) owns no GPU resources; this
// module is the one shell file that does, because a run's meshes die with the
// run — `dispose()` unsubscribes and frees them.

import { createMesh, type Mesh } from "../webgl";
import { buildGhostGeometry, buildGridGeometry } from "../grid";
import { createWorld, type World, type WorldOptions } from "../game/world";

export interface Session {
  readonly world: World;
  /** Exposed-face cube mesh; the reference changes when a rebuild fires. */
  readonly cube: Mesh;
  /** Translucent ghost shells, drawn in the blended pass. */
  readonly ghosts: Mesh;
  /** Rebuild the meshes if any block changed since the last call. */
  rebuildIfDirty(): void;
  dispose(): void;
}

export function createSession(gl: WebGL2RenderingContext, seed: number, opts: WorldOptions = {}): Session {
  const world = createWorld(seed, opts);
  let cube = createMesh(gl, buildGridGeometry(world.blocks));
  let ghosts = createMesh(gl, buildGhostGeometry(world.blocks));
  // A state change only marks dirty, so a burst of changes costs one rebuild
  // in the next frame (same policy main.ts had when it owned the meshes).
  let dirty = false;
  const unsubscribe = world.events.on("blockChanged", () => {
    dirty = true;
  });
  return {
    world,
    get cube() {
      return cube;
    },
    get ghosts() {
      return ghosts;
    },
    rebuildIfDirty() {
      if (!dirty) return;
      cube.dispose();
      ghosts.dispose();
      cube = createMesh(gl, buildGridGeometry(world.blocks));
      ghosts = createMesh(gl, buildGhostGeometry(world.blocks));
      dirty = false;
    },
    dispose() {
      unsubscribe();
      cube.dispose();
      ghosts.dispose();
    },
  };
}
